/**
 * Node customizado para ações (tags, atualizar contato, transferências)
 * Design genérico que se adapta ao tipo de ação
 * Inclui toolbar flutuante ao selecionar
 */

import { Handle, Position, NodeProps } from '@xyflow/react';
import { Tag, MinusCircle, UserCog, Users, ArrowRightLeft, MousePointerClick } from 'lucide-react';
import { FlowNodeData } from '@/types/flows';
import { FlowNodeToolbar } from './FlowNodeToolbar';

/**
 * Componente visual do node de ação
 */
export function ActionNode({ data, id, selected }: NodeProps) {
  const nodeData = data as FlowNodeData;
  
  // Callbacks injetados via FlowCanvas
  const onEdit = (nodeData as any).onEdit;
  const onDuplicate = (nodeData as any).onDuplicate;
  const onDelete = (nodeData as any).onDelete;
  const onTestFromHere = (nodeData as any).onTestFromHere;
  
  // Define ícone e label baseado no tipo
  const getActionConfig = () => {
    switch (nodeData.type) {
      case 'add_tag':
        // Suporta escopo: contato ou negócio (CRM)
        const addTagScope = nodeData.config?.scope || 'contact';
        const addTagName = nodeData.config?.tag_name || nodeData.config?.tagName || 'Tag não configurada';
        
        return {
          icon: Tag,
          label: addTagScope === 'deal' ? 'Tag de Negócio' : 'Adicionar Tag',
          color: 'purple',
          detail: addTagScope === 'deal' 
            ? `${addTagName} (CRM)` 
            : addTagName
        };
      case 'remove_tag':
        // Monta detalhe baseado no modo e escopo
        const scope = nodeData.config?.scope || 'contact';
        const mode = nodeData.config?.mode || 'specific';
        let removeDetail = 'Não configurado';
        
        if (mode === 'all') {
          removeDetail = scope === 'contact' 
            ? '🗑️ Todas (Contato)'
            : '🗑️ Todas (Negócio)';
        } else if (nodeData.config?.tag_name) {
          removeDetail = scope === 'contact'
            ? nodeData.config.tag_name
            : `${nodeData.config.tag_name} (Negócio)`;
        }
        
        return {
          icon: MinusCircle,
          label: 'Remover Tag',
          color: 'rose',
          detail: removeDetail
        };
      case 'update_contact':
        return {
          icon: UserCog,
          label: 'Atualizar Contato',
          color: 'indigo',
          detail: nodeData.config?.field || 'Campo não configurado'
        };
      case 'transfer_human':
        // Detecta modo de atribuição para exibir info correta
        const assignmentMode = nodeData.config?.assignment_mode || 'manual';
        const agentName = nodeData.config?.agent_name;
        const queueName = nodeData.config?.distribution_queue_name;
        let transferDetail = 'Não configurado';
        
        if (assignmentMode === 'previous_agent') {
          transferDetail = '🔄 Último agente';
        } else if (assignmentMode === 'distribution_queue' && queueName) {
          // NOVO: Exibe nome da fila de distribuição
          const forceRedist = nodeData.config?.force_redistribute;
          transferDetail = `⚡ Fila: ${queueName}`;
          if (forceRedist) {
            transferDetail += ' (redistribui)';
          }
        } else if (agentName) {
          transferDetail = agentName;
        }
        
        return {
          icon: ArrowRightLeft,
          label: 'Atribuir Humano',
          color: 'teal',
          detail: transferDetail
        };
      case 'transfer_department':
        return {
          icon: Users,
          label: 'Transferir para Depto',
          color: 'cyan',
          detail: nodeData.config?.department_name || 'Departamento'
        };
      case 'send_button_link':
        // Exibe label do botão e URL truncada
        const btnLabel = nodeData.config?.button_label || 'Botão';
        const btnUrl = nodeData.config?.link_url 
          ? (nodeData.config.link_url.length > 30 
            ? nodeData.config.link_url.substring(0, 30) + '...' 
            : nodeData.config.link_url)
          : 'URL não configurada';
        return {
          icon: MousePointerClick,
          label: 'Botão Link',
          color: 'violet',
          detail: `🔗 ${btnLabel} → ${btnUrl}`
        };
      default:
        return {
          icon: Tag,
          label: 'Ação',
          color: 'gray',
          detail: ''
        };
    }
  };

  const config = getActionConfig();
  const Icon = config.icon;

  return (
    <div className="bg-card rounded-lg shadow-lg border-2 border-purple-500 min-w-[200px] max-w-[240px]">
      {/* Toolbar flutuante */}
      <FlowNodeToolbar
        nodeId={id}
        nodeType={nodeData.type}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        onTestFromHere={onTestFromHere}
        isVisible={selected}
      />
      
      <Handle
        type="target"
        position={Position.Top}
        className="!bg-purple-500 !w-3 !h-3"
      />
      
      <div className="p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="bg-purple-500/10 text-purple-600 p-2 rounded">
            <Icon className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm text-foreground">
              {nodeData.config?.nickname || config.label}
            </div>
          </div>
        </div>
        
        {config.detail && (
          <div className="text-xs text-muted-foreground bg-muted/50 rounded p-2">
            {config.detail}
          </div>
        )}
      </div>
      
      <Handle
        type="source"
        position={Position.Bottom}
        className="!bg-purple-500 !w-3 !h-3"
      />
    </div>
  );
}
